import { z } from 'zod';
import { ChannelKindSchema, LinkStatusSchema, PersonStatusSchema } from './enums';
import { PageQuerySchema } from './common';

/**
 * §5, §7.2, §7.3 ТЗ. Администраторы заказчика: персона, её связи с объектами
 * и подтверждённые идентификаторы в мессенджерах.
 */

/** Связь «персона ↔ объект». История сохраняется всегда: отзыв — это статус, а не удаление (§13). */
export const PersonLinkSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  projectName: z.string(),
  status: LinkStatusSchema,
  preferredChannel: ChannelKindSchema.nullable(),
  registeredAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
});
export type PersonLink = z.infer<typeof PersonLinkSchema>;

/** §16.1: единственное средство опознания в боте. Отображаемое имя им не является. */
export const PersonIdentitySchema = z.object({
  channel: ChannelKindSchema,
  displayName: z.string().nullable(),
  confirmedAt: z.string().datetime(),
});
export type PersonIdentity = z.infer<typeof PersonIdentitySchema>;

export const PersonSummarySchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  status: PersonStatusSchema,
  /** §7.2: согласие на обработку ПДн, подтверждённое при регистрации. */
  consentAt: z.string().datetime().nullable(),
  /** Пометка «возможный дубль», поставленная автоматически. Слияния она не выполняет. */
  duplicateOfId: z.string().uuid().nullable(),
  /** Заполнено — персона слита в другую и в работе больше не участвует. */
  mergedIntoId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  links: z.array(PersonLinkSchema),
});
export type PersonSummary = z.infer<typeof PersonSummarySchema>;

/** Кандидат на дубль: показывается сотруднику, решение принимает он (§7.2). */
export const DuplicateCandidateSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  status: PersonStatusSchema,
  /** Что именно совпало. ФИО в этот список не входит и войти не может (§7.2). */
  matchedBy: z.array(z.enum(['PHONE', 'EMAIL'])).min(1),
  projectNames: z.array(z.string()),
});
export type DuplicateCandidate = z.infer<typeof DuplicateCandidateSchema>;

export const PersonDetailSchema = PersonSummarySchema.extend({
  identities: z.array(PersonIdentitySchema),
  duplicateCandidates: z.array(DuplicateCandidateSchema),
});
export type PersonDetail = z.infer<typeof PersonDetailSchema>;

export const ListPeopleQuerySchema = PageQuerySchema.extend({
  projectId: z.string().uuid().optional(),
  status: PersonStatusSchema.optional(),
  linkStatus: LinkStatusSchema.optional(),
  /** Поиск по ФИО, телефону и почте. На сопоставление персон не влияет. */
  search: z.string().trim().min(1).max(150).optional(),
  /**
   * Только персоны с непросмотренной пометкой возможного дубля.
   * Строка из query, не coerce: `Boolean('false') === true` — фильтр
   * «нет» превратился бы в «да».
   */
  duplicatesOnly: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});
export type ListPeopleQuery = z.infer<typeof ListPeopleQuerySchema>;

/** §7.2: слияние — ручная операция сотрудника, с записью в аудит. */
export const MergePersonsRequestSchema = z.object({
  /** Персона, в которую вливаем. Она остаётся действующей. */
  targetPersonId: z.string().uuid(),
});
export type MergePersonsRequest = z.infer<typeof MergePersonsRequestSchema>;

export const BlockPersonRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type BlockPersonRequest = z.infer<typeof BlockPersonRequestSchema>;

export const RevokeLinkRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type RevokeLinkRequest = z.infer<typeof RevokeLinkRequestSchema>;
